import React, { useState, useEffect } from "react";
import { User, IDataPagination } from "../../types/User";
import Button from "../ui/Button";
import UserItem from "./UserItem";
import UserForm from "./UserForm";
import { useFetchContext } from "../../context/FetchContext";
import { useToast } from "../../context/ToastContext";

const UserList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const {
    fetchData,
    state: { isLoading },
  } = useFetchContext();
  const { showToast } = useToast();

  // Fetch users from API
  useEffect(() => {
    const getUsers = async () => {
      try {
        const response = await fetchData<IDataPagination<User>>("/users");
        if (response && response.data && response.data.items) {
          setUsers(response.data.items);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        showToast("Errore durante il caricamento degli utenti", "error");
      }
    };

    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddUser = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleSaveUser = async (user: User) => {
    try {
      if (editingUser) {
        // Update existing user
        const response = await fetchData<User>(
          `/users/${user.uuid}`,
          "PUT",
          user
        );
        if (response && response.data) {
          setUsers(
            users.map((u) =>
              u.uuid === response.data.uuid ? response.data : u
            )
          );
          showToast("Utente aggiornato con successo", "success");
        }
      } else {
        // Add new user
        const response = await fetchData<User>("/users", "POST", user);
        if (response && response.data) {
          setUsers([...users, response.data]);
          showToast("Utente creato con successo", "success");
        }
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error saving user:", error);
      showToast("Errore durante il salvataggio dell'utente", "error");
    }
  };

  const handleDeleteUser = async (uuid: string) => {
    try {
      await fetchData(`/users/${uuid}`, "DELETE");
      setUsers(users.filter((user) => user.uuid !== uuid));
      showToast("Utente eliminato con successo", "success");
    } catch (error) {
      console.error("Error deleting user:", error);
      showToast("Errore durante l'eliminazione dell'utente", "error");
    }
  };

  return (
    <div className="overflow-hidden bg-white shadow sm:rounded-lg">
      <div className="flex justify-between items-center px-4 py-5 sm:px-6">
        <h2 className="text-lg font-medium text-gray-900">Lista Utenti</h2>
        <Button onClick={handleAddUser}>Aggiungi Utente</Button>
      </div>
      <div className="border-t border-gray-200">
        {isLoading ? (
          <div className="text-center py-4">
            <svg
              className="animate-spin h-8 w-8 mx-auto text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="mt-2 text-gray-500">Caricamento utenti...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-gray-500">Nessun utente trovato</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Nome
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Email
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Magazzino
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Ruolo
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <UserItem
                    key={user.uuid}
                    user={user}
                    onEdit={() => handleEditUser(user)}
                    onDelete={() => handleDeleteUser(user.uuid || "")}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <UserForm
          user={editingUser}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveUser}
        />
      )}
    </div>
  );
};

export default UserList;
